import React from "react";
import { DataTable as PrimeDataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./DataTable.scss";

/**
 * Componente DataTable reutilizable
 * @param {Object} props
 * @param {Array} props.data - Array de datos a mostrar en la tabla
 * @param {Array} props.columns - Array de columnas con formato: [{ field: 'nombre', header: 'Nombre', sortable: true, body: (rowData) => {...} }]
 * @param {Object} props.pagination - Objeto con datos de paginación de la API { total, current_page, per_page, total_pages }
 * @param {Function} props.onPageChange - Callback cuando cambia la página (recibe el número de página)
 * @param {Boolean} props.loading - Estado de carga
 * @param {String} props.emptyMessage - Mensaje cuando no hay datos
 * @param {Boolean} props.showActions - Mostrar columna de acciones
 * @param {Function} props.onEdit - Callback para editar (recibe rowData)
 * @param {Function} props.onDelete - Callback para eliminar (recibe rowData)
 * @param {Function} props.onRowClick - Callback cuando se hace click en una fila (recibe rowData) - OPCIONAL
 * @param {String} props.className - Clases CSS adicionales
 * @param {Boolean} props.striped - Filas alternadas
 * @param {Boolean} props.hoverable - Efecto hover en filas
 * @param {Array} props.rowsPerPageOptions - Opciones de filas por página
 * @param {String} props.responsiveLayout - Layout responsive ('scroll' o 'stack')
 * @param {Boolean} props.showStatusFilter - Mostrar filtros de estado (Todo, Activos, Inactivos)
 * @param {Function} props.onStatusFilterChange - Callback cuando cambia el filtro de estado (recibe 'all', 'activo', 'inactivo')
 * @param {String} props.currentStatusFilter - Filtro de estado actual ('all', 'activo', 'inactivo')
 */
const DataTable = ({
    data = [],
    columns = [],
    pagination = null,
    onPageChange = null,
    loading = false,
    emptyMessage = "No hay datos disponibles",
    showActions = false,
    onEdit = null,
    onDelete = null,
    onRowClick = null,
    className = "",
    striped = true,
    hoverable = true,
    rowsPerPageOptions = [5, 10, 25, 50],
    responsiveLayout = "scroll",
    actionColumnWidth = "120px",
    editTooltip = "Editar",
    deleteTooltip = "Eliminar",
    showStatusFilter = false,
    onStatusFilterChange = null,
    currentStatusFilter = "all",
}) => {
    // Template para las acciones
    const actionsBodyTemplate = (rowData) => {
        return (
            <div className="data-table__actions">
                {onEdit && (
                    <Button
                        icon="pi pi-pencil"
                        className="data-table__actions-button data-table__actions-button--edit"
                        onClick={() => onEdit(rowData)}
                        tooltip={editTooltip}
                        tooltipOptions={{ position: "top" }}
                    />
                )}
                {onDelete && (
                    <Button
                        icon="pi pi-trash"
                        className="data-table__actions-button data-table__actions-button--delete"
                        onClick={() => onDelete(rowData)}
                        tooltip={deleteTooltip}
                        tooltipOptions={{ position: "top" }}
                    />
                )}
            </div>
        );
    };

    // Manejo de paginación
    const handlePageChange = (event) => {
        if (onPageChange && pagination) {
            // event.page es 0-indexed, la API espera 1-indexed
            onPageChange(event.page + 1, event.rows);
        }
    };

    // Manejo de click en fila
    const handleRowClick = (event) => {
        if (onRowClick) {
            onRowClick(event.data);
        }
    };

    // Calcular total de registros
    const totalRecords = pagination ? pagination.total : data.length;

    return (
        <div className={`data-table ${className}`}>
            {/* Filtros de estado */}
            {showStatusFilter && onStatusFilterChange && (
                <div className="data-table__status-filter">
                    <button
                        className={`data-table__status-btn ${
                            currentStatusFilter === "all"
                                ? "data-table__status-btn--active"
                                : ""
                        }`}
                        onClick={() => onStatusFilterChange("all")}
                    >
                        <i className="pi pi-list"></i>
                        <span>Todo</span>
                    </button>
                    <button
                        className={`data-table__status-btn data-table__status-btn--activo ${
                            currentStatusFilter === "activo"
                                ? "data-table__status-btn--active"
                                : ""
                        }`}
                        onClick={() => onStatusFilterChange("activo")}
                    >
                        <i className="pi pi-check-circle"></i>
                        <span>Activos</span>
                    </button>
                    <button
                        className={`data-table__status-btn data-table__status-btn--inactivo ${
                            currentStatusFilter === "inactivo"
                                ? "data-table__status-btn--active"
                                : ""
                        }`}
                        onClick={() => onStatusFilterChange("inactivo")}
                    >
                        <i className="pi pi-times-circle"></i>
                        <span>Inactivos</span>
                    </button>
                </div>
            )}

            {/* Contador de registros */}
            {totalRecords > 0 && (
                <div className="data-table__total">
                    {totalRecords}{" "}
                    {totalRecords === 1 ? "registro" : "registros"}
                </div>
            )}

            <div className="data-table__container">
                <PrimeDataTable
                    value={data}
                    className={`data-table__table ${
                        onRowClick ? "data-table__table--clickable" : ""
                    }`}
                    loading={loading}
                    emptyMessage={emptyMessage}
                    stripedRows={striped}
                    showGridlines={false}
                    responsiveLayout={responsiveLayout}
                    breakpoint="768px"
                    paginator={!pagination} // Si no hay paginación de API, usar la interna
                    rows={!pagination ? 10 : undefined}
                    rowsPerPageOptions={
                        !pagination ? rowsPerPageOptions : undefined
                    }
                    onRowClick={onRowClick ? handleRowClick : undefined}
                    selectionMode={onRowClick ? "single" : undefined}
                >
                    {columns.map((col, index) => (
                        <Column
                            key={index}
                            field={col.field}
                            header={col.header}
                            body={col.body}
                            sortable={col.sortable !== false}
                            className="data-table__column"
                            style={col.style}
                        />
                    ))}
                    {showActions && (
                        <Column
                            body={actionsBodyTemplate}
                            header="Acciones"
                            className="data-table__column data-table__column--actions"
                            style={{
                                width: actionColumnWidth,
                                textAlign: "center",
                            }}
                        />
                    )}
                </PrimeDataTable>
            </div>

            {/* Paginación personalizada para API */}
            {pagination && (
                <Paginator
                    first={(pagination.current_page - 1) * pagination.per_page}
                    rows={pagination.per_page}
                    totalRecords={pagination.total}
                    onPageChange={handlePageChange}
                    rowsPerPageOptions={rowsPerPageOptions}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={`Mostrando {first} a {last} de {totalRecords} registros`}
                    className="data-table__paginator"
                />
            )}
        </div>
    );
};

export default DataTable;
